import React, { useCallback } from 'react';

import DialogContent from 'components/DialogContent';
import { H2 } from 'components/Heading';
import { ModalFooterButton } from 'components/Modal';
import { InstagramSocialCircle } from 'components/SocialCircle';
import { Step } from 'components/SteppedModal';
import useInstagramAuthenticator, {
  UseInstagramAuthenticatorConfig,
} from 'hooks/useInstagramAuthenticator';
import { block } from '../utils';
import { InstagramPostModalStep } from './types';

interface UseAuthorizationStepConfig
  extends Pick<UseInstagramAuthenticatorConfig, 'onAuthSuccess' | 'onError'> {
  onAuth: () => void;
}

export default function useAuthorizationStep({
  onAuth,
  onAuthSuccess,
  onError,
}: UseAuthorizationStepConfig): Step<InstagramPostModalStep> {
  const { authenticate, authenticating } = useInstagramAuthenticator({
    force: true,
    onAuthSuccess,
    onError,
  });

  const handleAuthenticate = useCallback((): void => {
    onAuth();
    authenticate();
  }, [authenticate, onAuth]);

  return {
    id: 'authorization',
    fluidFooterButtons: true,
    component: (
      <DialogContent
        title="Let's add your account"
        details={
          <H2>
            We'll quickly send you to Instagram so you can give us permission to
            add videos to your account.
          </H2>
        }
        icon={<InstagramSocialCircle className={block('icon')} />}
        detailsClassName={block('details')}
      />
    ),
    renderFooterButtons: ({ submit }) => [
      <ModalFooterButton
        {...submit}
        theme="submit"
        disabled={authenticating}
        onClick={handleAuthenticate}
      >
        <span>Sign in with Instagram</span>
      </ModalFooterButton>,
    ],
  };
}
